import {useMemo, useEffect, useCallback, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  handleCurrentStep,
  selectSelectedBanData,
  selectServerData,
  setSelectedBansForPaymentUpdate,
} from '../DebtAndUpdatePaymentBlockSlice';
import {buildDisplayBansForUpdatePaymentSelect, hasHiddenDebtBans} from '../utils/updatePaymentBanUtils';

export const useSelectUpdatePaymentBan = () => {
  const dispatch = useDispatch();
  const selectedBanData = useSelector(selectSelectedBanData);
  const allBans = useSelector(selectServerData);
  const selectedBansForPaymentUpdate = useSelector((state) => state.debtAndUpdatePayment.selectedBansForPaymentUpdate);

  const displayBans = useMemo(
    () => buildDisplayBansForUpdatePaymentSelect(allBans, selectedBanData?.banNumber),
    [allBans, selectedBanData?.banNumber],
  );

  const showDebtAccountsTooltip = useMemo(() => hasHiddenDebtBans(allBans), [allBans]);
  const defaultSelectionInitialized = useRef(false);

  useEffect(() => {
    if (defaultSelectionInitialized.current) return;

    const banNumber = selectedBanData?.banNumber;
    if (!banNumber) return;

    const isEligible = displayBans.some((ban) => ban.banNumber === banNumber);
    if (!isEligible) return;

    if (!selectedBansForPaymentUpdate?.length) {
      dispatch(setSelectedBansForPaymentUpdate([banNumber]));
    }

    defaultSelectionInitialized.current = true;
  }, [selectedBanData?.banNumber, displayBans, dispatch, selectedBansForPaymentUpdate]);

  const toggleBanSelection = useCallback(
    (banNumber, checked) => {
      const next = checked
        ? [...new Set([...(selectedBansForPaymentUpdate ?? []), banNumber])]
        : (selectedBansForPaymentUpdate ?? []).filter((b) => b !== banNumber);
      const ordered = displayBans.map((ban) => ban.banNumber).filter((n) => next.includes(n));
      dispatch(setSelectedBansForPaymentUpdate(ordered));
    },
    [selectedBansForPaymentUpdate, displayBans, dispatch],
  );

  const actions = {
    goToStep: (step) => dispatch(handleCurrentStep(step)),
    toggleBanSelection,
  };

  return {
    selectedBansForPaymentUpdate,
    displayBans,
    showDebtAccountsTooltip,
    actions,
  };
};
